import { findFilter, FilterParam, SearchFilter } from "@/components/borrower/search/filterRegistry";
import { formatValue, SearchToken, tokenize } from "@/components/borrower/search/searchTokens";
import { SearchFilterParams } from "@/types/borrower";

export type ParsedSearchQuery = SearchFilterParams;

/** The searched text of a token that is not a filter: unknown `key:value` pairs stay as typed. */
const freeText = (token: SearchToken) => (token.key === null ? token.values[0] : token.text);

function tokenParams(filter: SearchFilter, token: SearchToken): Partial<SearchFilterParams> {
  if (filter.toParams) return filter.toParams(token.values);
  const param = token.negated ? filter.excludeParam : filter.param;
  return param ? { [param]: token.values.map(filter.normalize ?? String) } : {};
}

/**
 * Parse a raw search string containing optional qualifier:value pairs
 * into structured API filter params. Comma lists mean any of the values;
 * a single value stays a plain string.
 *
 * Examples:
 *   "iso:toni status:declined,funded acme"
 *   -> { broker_company_name: "toni", app_status: ["declined", "funded"], search: "acme" }
 *
 *   "created:-13 -state:NY"
 *   -> { created_after: "2026-03-24", exclude_state: "NY" }
 */
export function parseSearchQuery(raw: string): ParsedSearchQuery {
  const text: string[] = [];
  const values = new Map<FilterParam, string[]>();

  for (const token of tokenize(raw)) {
    if (!token.filter) {
      const searched = freeText(token);
      if (searched) text.push(searched);
      continue;
    }
    if (token.negated && !token.filter.excludeParam) continue;
    for (const [param, value] of Object.entries(tokenParams(token.filter, token))) {
      if (value === undefined) continue;
      const key = param as FilterParam;
      const list = values.get(key) ?? [];
      // Lists add up across tokens; a single-valued filter keeps its first token.
      if (token.filter.multi || !list.length) values.set(key, [...list, ...[value].flat()]);
    }
  }

  const query: ParsedSearchQuery = {};
  if (text.length) query.search = text.join(" ");
  for (const [param, list] of values) {
    if (list.length) Object.assign(query, { [param]: list.length === 1 ? list[0] : list });
  }
  return query;
}

/** The tokens of a qualifier, by filter (aliases included) or, for an unknown key, by name. */
const tokensOf = (tokens: SearchToken[], key: string) => {
  const filter = findFilter(key);
  const name = key.toLowerCase();
  return tokens.filter((token) => (filter ? token.filter === filter : token.key === name));
};

/**
 * The value of a (non-negated) qualifier in a raw search string, comma lists
 * joined. Returns undefined if the qualifier is not present.
 */
export function extractQualifier(raw: string, key: string): string | undefined {
  const token = tokensOf(tokenize(raw), key).find((t) => !t.negated && t.values.length);
  return token?.values.join(",");
}

/** The qualifier's value when it is exactly one included value, else undefined. */
export function soleQualifierValue(raw: string, key: string): string | undefined {
  const tokens = tokensOf(tokenize(raw), key);
  const [token] = tokens;
  return tokens.length === 1 && !token.negated && token.values.length === 1
    ? token.values[0]
    : undefined;
}

/**
 * Inject a qualifier into the search string.
 * Only appends if the qualifier is NOT already present (search bar wins).
 */
export function injectQualifier(raw: string, key: string, value: string): string {
  if (extractQualifier(raw, key) !== undefined) return raw;
  const prefix = raw.trim() ? `${raw.trim()} ` : "";
  return `${prefix}${key}:${formatValue(value)}`;
}

/** Remove every token of a qualifier, negated ones included. */
export function removeQualifier(raw: string, key: string): string {
  const tokens = tokenize(raw);
  const removed = new Set(tokensOf(tokens, key));
  return tokens
    .filter((token) => !removed.has(token))
    .map((token) => token.text)
    .join(" ");
}
